import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Collapse } from '@mui/material';
import { ChevronDown, ChevronRight, Circle } from 'lucide-react';
import { MODULE_CONFIGS, type ModuleId } from '../theme/moduleColors';

export interface NavSection { title: string; items: NavItem[]; collapsed?: boolean; }
export interface NavItem { path: string; label: string; icon?: React.ReactElement; }
export type ModuleNavigation = NavSection[];

interface ModuleSidebarProps { moduleId: ModuleId; navigation: ModuleNavigation; }

const ModuleSidebar: React.FC<ModuleSidebarProps> = ({ moduleId, navigation }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = MODULE_CONFIGS[moduleId];
  const [openSections, setOpenSections] = React.useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    navigation.forEach((s) => { init[s.title] = !s.collapsed; });
    return init;
  });
  const toggleSection = (title: string) => { setOpenSections((prev) => ({ ...prev, [title]: !prev[title] })); };
  const isActive = (path: string) => location.pathname === path;

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Box sx={{ px: 2, py: 1, mb: 1 }}>
        <Typography variant="subtitle2" sx={{ color: config.accent, fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{config.label}</Typography>
        <Typography variant="caption" sx={{ color: '#6B7280' }}>{config.description}</Typography>
      </Box>
      <Divider sx={{ borderColor: '#E5E7EB' }} />
      {navigation.map((section) => (
        <Box key={section.title}>
          <ListItem disablePadding sx={{ px: 1 }} onClick={() => toggleSection(section.title)}>
            <ListItemButton sx={{ borderRadius: 1, py: 0.5 }}>
              <ListItemText primary={section.title} primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em' }} />
              {openSections[section.title] ? <ChevronDown size={14} color="#9CA3AF" /> : <ChevronRight size={14} color="#9CA3AF" />}
            </ListItemButton>
          </ListItem>
          <Collapse in={openSections[section.title]} timeout="auto" unmountOnExit>
            <List disablePadding>
              {section.items.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ px: 1 }}>
                  <ListItemButton onClick={() => navigate(item.path)} selected={isActive(item.path)} sx={{
                    borderRadius: 1, mb: 0.25, py: 0.5, pl: 3,
                    '&.Mui-selected': { bgcolor: `${config.accent}14`, '&:hover': { bgcolor: `${config.accent}20` }, '& .MuiListItemIcon-root': { color: config.accent }, '& .MuiListItemText-primary': { color: config.accent, fontWeight: 600 } },
                  }}>
                    <ListItemIcon sx={{ minWidth: 28, color: isActive(item.path) ? config.accent : '#9CA3AF' }}>
                      {item.icon || <Circle size={6} />}
                    </ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: isActive(item.path) ? 600 : 400 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      ))}
    </Box>
  );
};

export default ModuleSidebar;
